// OrgIdentitiesPage — cross-tenant user aggregate view (BE-21).
//
// Lists every user across every active tenant in the org via
// GET /api/v1/organizations/{id}/users. Per-user edit/delete is a
// link-out to the per-tenant dp-admin-console (decision D7 — write
// stays per-tenant; only the cross-tenant read lives here).
//
// Failure tolerance: the backend handler emits 200 even when some
// tenants are unreachable; per_tenant_errors in the meta field
// surfaces which. We render a soft warning + the partial list so a
// single down tenant doesn't blank the page.

import type { components } from '@intelliauth/api-client-v2-cp/types'
import { Badge, DataTable, EmptyState, Input } from '@intelliauth/ui'
import type { DataTableColumn } from '@intelliauth/ui'
import { AlertTriangle, Contact, ExternalLink, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'
import { tenantBaseUrl } from '@/lib/domain'

type CrossTenantUser = components['schemas']['CrossTenantUser']

export default function OrgIdentitiesPage() {
  const { currentOrg } = useAuth()
  const [users, setUsers] = useState<CrossTenantUser[]>([])
  const [perTenantErrors, setPerTenantErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tenantFilter, setTenantFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  const fetchUsers = useCallback(async () => {
    if (!currentOrg) return
    setLoading(true)
    setError(null)
    try {
      const env = await cpClient().get('/api/v1/organizations/{id}/users', {
        path: { id: currentOrg.id },
      })
      const body = env as {
        data?: CrossTenantUser[]
        meta?: { per_tenant_errors?: Record<string, string> }
      }
      setUsers(body.data ?? [])
      setPerTenantErrors(body.meta?.per_tenant_errors ?? {})
    } catch (e) {
      setError(e instanceof CpV2ApiError ? e.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [currentOrg])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const tenantOptions = useMemo(() => {
    const m = new Map<string, string>()
    for (const u of users) m.set(u.tenant_id, u.tenant_name)
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }))
  }, [users])

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return users.filter((u) => {
      if (tenantFilter && u.tenant_id !== tenantFilter) return false
      if (q && !u.email.toLowerCase().includes(q)) return false
      return true
    })
  }, [users, tenantFilter, searchQuery])

  const columns: DataTableColumn<CrossTenantUser>[] = [
    {
      key: 'email',
      header: 'Email',
      cell: (u) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-medium">
            {u.email[0]?.toUpperCase() ?? '?'}
          </div>
          <span className="text-sm font-medium text-foreground">{u.email}</span>
        </div>
      ),
    },
    {
      key: 'tenant',
      header: 'Tenant',
      width: '200px',
      cell: (u) => (
        <Badge variant="info" className="font-mono text-xs">
          {u.tenant_name}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '180px',
      cell: (u) => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {new Date(u.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '160px',
      align: 'right',
      cell: (u) => {
        if (!currentOrg) return null
        // Per D7: write actions (edit / delete) live in dp-admin-console.
        // We link out by tenant slug; the user_id is a DP-scoped id so
        // we can deep-link straight to that user's detail page.
        const tenant = users.find((x) => x.tenant_id === u.tenant_id)
        if (!tenant) return null
        const adminUrl = `${tenantBaseUrl(tenantSlugFromUser(tenant.tenant_name), currentOrg.slug)}/admin/users/${u.user_id}`
        return (
          <a
            href={adminUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
          >
            Edit in tenant
            <ExternalLink className="h-3 w-3" />
          </a>
        )
      },
    },
  ]

  const errorEntries = Object.entries(perTenantErrors)

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <Contact className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Identities</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Every user across every active tenant. Per-user edit and delete live in each tenant's
          admin console — open the row's link to switch context.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {errorEntries.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Partial results</p>
            <p className="text-xs mt-1">
              {errorEntries.length} tenant{errorEntries.length === 1 ? '' : 's'} could not be
              reached. The list below excludes them.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email"
            className="pl-10"
          />
        </div>
        {tenantOptions.length > 1 && (
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="bg-card border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All tenants</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <DataTable<CrossTenantUser>
        columns={columns}
        rows={filteredUsers}
        getRowId={(u) => `${u.tenant_id}:${u.user_id}`}
        loading={loading}
        empty={
          <EmptyState
            icon={Contact}
            title={
              users.length === 0 ? 'No users yet' : `No users match "${searchQuery || tenantFilter}"`
            }
            description={
              users.length === 0
                ? 'Users created in any active tenant will appear here.'
                : 'Try clearing the search or tenant filter.'
            }
          />
        }
      />
    </div>
  )
}

// CrossTenantUser carries tenant_name but not tenant_slug. The
// per-tenant link-out needs the slug to build the URL. The slug
// matches the name in practice for cuke* + canonical tenants, so
// we approximate by lowercasing + stripping non-slug chars. A future
// backend change to include tenant_slug on CrossTenantUser would
// make this exact.
function tenantSlugFromUser(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
